import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import StatTile from "shell/StatTile";

import useGroupDueData, { MONTHS } from "layouts/tables/data/groupDueTableData";

function GroupDues() {
  const {
    columns,
    rows,
    isLoading,
    error,
    summary,
    canManage,
    departments,
    ministries,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedDue,
    setSelectedDue,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddDue,
  } = useGroupDueData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading group dues...</Typography>;
  }
  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const groupOptions = selectedDue?.groupType === "Department" ? departments : ministries;

  return (
    <Box>
      <PageHeader
        title="Department & Ministry Dues"
        subtitle="Monthly dues owed by each department/ministry to the church — separate from member-to-group dues."
        actions={
          <>
            {canManage && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddDue}>
                New Due
              </Button>
            )}
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="receipt_long"
            label="Total Due"
            value={`GHS ${Number(summary.total_due || 0).toLocaleString()}`}
            sub={`${summary.count || 0} entries`}
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="task_alt"
            label="Total Remitted"
            value={`GHS ${Number(summary.total_paid || 0).toLocaleString()}`}
            sub="paid to church"
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="pending_actions"
            label="Outstanding"
            value={`GHS ${Number(
              (summary.total_due || 0) - (summary.total_paid || 0)
            ).toLocaleString()}`}
            sub="not yet remitted"
            tone="warning"
          />
        </Grid>
      </Grid>

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search dues..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedDue?.id ? "Edit Due" : "New Due"}
        onSave={handleEditSubmit}
        saveLabel={selectedDue?.id ? "Save Changes" : "Create Due"}
        maxWidth="sm"
      >
        <FormSection>
          {!selectedDue?.id && (
            <>
              <TextField
                select
                label="Group Type"
                value={selectedDue?.groupType || "Department"}
                onChange={(e) =>
                  setSelectedDue({ ...selectedDue, groupType: e.target.value, groupRefId: "" })
                }
                fullWidth
                margin="normal"
              >
                <MenuItem value="Department">Department</MenuItem>
                <MenuItem value="Ministry">Ministry</MenuItem>
              </TextField>
              <TextField
                select
                label={selectedDue?.groupType === "Department" ? "Department" : "Ministry"}
                value={selectedDue?.groupRefId || ""}
                onChange={(e) => setSelectedDue({ ...selectedDue, groupRefId: e.target.value })}
                fullWidth
                margin="normal"
                required
              >
                {groupOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <TextField
            label="Amount (GHS)"
            type="number"
            value={selectedDue?.amount || ""}
            onChange={(e) => setSelectedDue({ ...selectedDue, amount: e.target.value })}
            fullWidth
            margin="normal"
            required
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              label="Due Month"
              value={selectedDue?.dueMonth || 1}
              onChange={(e) => setSelectedDue({ ...selectedDue, dueMonth: Number(e.target.value) })}
              fullWidth
              margin="normal"
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Due Year"
              type="number"
              value={selectedDue?.dueYear || new Date().getFullYear()}
              onChange={(e) => setSelectedDue({ ...selectedDue, dueYear: Number(e.target.value) })}
              fullWidth
              margin="normal"
            />
          </Box>

          <TextField
            select
            label="Status"
            value={selectedDue?.status || "pending"}
            onChange={(e) => setSelectedDue({ ...selectedDue, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
          </TextField>

          {selectedDue?.status === "paid" && (
            <TextField
              label="Paid Date"
              type="date"
              value={selectedDue?.paidDate || ""}
              onChange={(e) => setSelectedDue({ ...selectedDue, paidDate: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          )}

          <TextField
            label="Notes"
            value={selectedDue?.notes || ""}
            onChange={(e) => setSelectedDue({ ...selectedDue, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete the due for "${selectedDue?.groupName}" (${selectedDue?.period})?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default GroupDues;
